/**
 * useLayoutStyles.js - Hook para generar estilos base de componentes layout
 * 
 * Este hook centraliza la lógica de estilos comunes entre todos los componentes
 * layout (Header, SidebarLeft, SidebarRight, ContentArea, Footer)
 */

import { useMemo } from 'react';
import { useTheme } from './useTheme';

/**
 * Hook para obtener estilos base de componentes layout
 * @param {boolean} isDarkMode - Estado del modo oscuro
 * @param {string} componentType - Tipo de componente ('header', 'sidebar', 'content', 'footer')
 * @param {Object} customStyles - Estilos personalizados adicionales
 * @returns {Object} Estilos del componente
 */
export const useLayoutStyles = (isDarkMode, componentType, customStyles = {}) => {
  const theme = useTheme(isDarkMode);

  return useMemo(() => {
    // Estilos base comunes para todos los componentes layout
    const baseStyles = {
      fontFamily: theme.typography.fontFamily.primary,
      transition: 'background-color 0.3s ease-in-out, border-color 0.3s ease-in-out',
      boxSizing: 'border-box',
      ...customStyles
    };

    // Estilos específicos por tipo de componente
    switch (componentType) {
      case 'header':
        return {
          ...baseStyles,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '64px',
          backgroundColor: theme.colors.primary,
          borderBottom: `1px solid ${theme.colors.border.primary}`,
          boxShadow: theme.colors.shadow.sm,
          zIndex: theme.zIndex.fixed,
          display: 'flex',
          alignItems: 'center',
          padding: `0 ${theme.spacing.lg}`,
          transition: 'background-color 0.3s ease-in-out, border-color 0.3s ease-in-out',
          ...customStyles
        };

      case 'sidebar-left':
        return {
          ...baseStyles,
          position: 'fixed',
          top: '64px',
          left: 0,
          backgroundColor: theme.colors.secondary,
          borderRight: `1px solid ${theme.colors.border.primary}`,
          overflowY: 'auto',
          padding: theme.spacing.lg,
          zIndex: theme.zIndex.sticky,
          transition: 'background-color 0.3s ease-in-out, border-color 0.3s ease-in-out, width 0.2s ease-in-out',
          ...customStyles
        };

      case 'sidebar-right':
        return {
          ...baseStyles,
          position: 'fixed',
          top: '64px',
          backgroundColor: theme.colors.primary,
          borderLeft: `1px solid ${theme.colors.border.primary}`,
          boxShadow: theme.colors.shadow.lg,
          overflowY: 'auto',
          overflowX: 'hidden',
          fontFamily: theme.typography.fontFamily.primary,
          zIndex: theme.zIndex.sticky,
          transition: 'width 0.3s ease-in-out, padding 0.3s ease-in-out, background-color 0.3s ease-in-out, border-color 0.3s ease-in-out',
          ...customStyles
        };

      case 'content':
        return {
          ...baseStyles,
          backgroundColor: theme.colors.primary,
          padding: theme.spacing.md, // Reducido 50% adicional (de lg a md)
          fontFamily: theme.typography.fontFamily.primary,
          lineHeight: theme.typography.lineHeight.relaxed,
          transition: 'margin-left 0.3s ease-in-out, margin-right 0.3s ease-in-out, background-color 0.3s ease-in-out',
          ...customStyles
        };

      case 'footer':
        return {
          ...baseStyles,
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '32px',
          backgroundColor: theme.colors.secondary,
          borderTop: `1px solid ${theme.colors.border.primary}`,
          display: 'flex',
          alignItems: 'center',
          padding: `0 ${theme.spacing.lg}`,
          fontSize: theme.typography.fontSize.xs,
          color: theme.colors.text.secondary,
          zIndex: theme.zIndex.fixed,
          ...customStyles
        };

      default:
        return baseStyles;
    }
  }, [theme, componentType, customStyles]);
};
